import { useState, useEffect } from 'react';
import axios from 'axios';
import { useParams } from 'react-router-dom';
import { X, Users, Search, User } from 'lucide-react';
import { Link } from 'react-router-dom';
import ProfileHeader from '@/components/profile/ProfileHeader';
import ProfileTabs from '@/components/profile/ProfileTabs';
import FriendsList from '@/components/profile/FriendsList';
import { HighlightsRow, CreateHighlightModal, HighlightViewer } from '@/components/stories/StoryHighlights';
import { toast } from 'sonner';
import './NewProfilePage.css';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Devotees Modal Component
const DevoteesModal = ({ userId, isOwner, onClose, friendsCount }) => {
  const [friends, setFriends] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');

  useEffect(() => {
    fetchFriends();
  }, [userId, search]);

  const fetchFriends = async () => {
    try {
      const token = localStorage.getItem('dharmbook_token');
      const endpoint = isOwner 
        ? `${API}/friends` 
        : `${API}/friends/user/${userId}`;
      
      const params = new URLSearchParams();
      if (search) params.set('search', search);
      
      const response = await axios.get(`${endpoint}?${params.toString()}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setFriends(response.data);
    } catch (error) {
      console.error('Failed to fetch friends:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleUnfriend = async (friendId, e) => {
    e.preventDefault();
    e.stopPropagation();
    
    if (!confirm('Are you sure you want to remove this devotee?')) return;
    
    try {
      const token = localStorage.getItem('dharmbook_token');
      await axios.delete(`${API}/friends/${friendId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Devotee removed');
      fetchFriends();
    } catch (error) {
      toast.error('Failed to remove devotee');
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50" onClick={onClose}>
      <div 
        className="bg-white rounded-2xl w-full max-w-lg mx-4 max-h-[80vh] flex flex-col shadow-2xl"
        onClick={e => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-gray-100">
          <div className="flex items-center gap-2">
            <Users className="w-5 h-5 text-[#FF9933]" />
            <h2 className="text-lg font-semibold text-gray-900">Devotees ({friendsCount})</h2>
          </div>
          <button 
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-full transition-colors"
            data-testid="close-devotees-modal"
          >
            <X className="w-5 h-5 text-gray-500" />
          </button>
        </div>

        {/* Search */}
        {friendsCount > 3 && (
          <div className="p-4 border-b border-gray-100">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search devotees..."
                className="w-full pl-10 pr-4 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-400"
                data-testid="devotees-search-input"
              />
            </div>
          </div>
        )}

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-4">
          {loading ? (
            <div className="flex justify-center py-8">
              <div className="w-8 h-8 border-2 border-[#FF9933] border-t-transparent rounded-full animate-spin" />
            </div>
          ) : friends.length === 0 ? (
            <div className="text-center py-12">
              <Users className="w-12 h-12 text-gray-300 mx-auto mb-3" />
              <p className="text-gray-500">{search ? 'No devotees found' : 'No devotees yet'}</p>
            </div>
          ) : (
            <div className="space-y-2">
              {friends.map((friend) => (
                <Link
                  key={friend.id}
                  to={`/profile/${friend.id}`}
                  onClick={onClose}
                  className="flex items-center gap-3 p-3 rounded-xl hover:bg-gray-50 transition-colors group"
                  data-testid={`devotee-${friend.id}`}
                >
                  {friend.avatar && !friend.avatar.includes('ui-avatars.com') ? (
                    <img
                      src={friend.avatar}
                      alt={friend.name}
                      className="w-12 h-12 rounded-full object-cover"
                    />
                  ) : (
                    <div className="w-12 h-12 rounded-full bg-gradient-to-br from-gray-100 to-gray-200 flex items-center justify-center">
                      <User className="w-6 h-6 text-gray-400" />
                    </div>
                  )}
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-gray-900 truncate">{friend.name}</p>
                    <p className="text-sm text-gray-500 capitalize">{friend.role}</p>
                  </div>
                  {isOwner && (
                    <button
                      onClick={(e) => handleUnfriend(friend.id, e)}
                      className="p-2 rounded-full bg-gray-100 opacity-0 group-hover:opacity-100 hover:bg-red-50 transition-all"
                      title="Remove devotee"
                    >
                      <X className="w-4 h-4 text-gray-500 hover:text-red-500" />
                    </button>
                  )}
                </Link>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default function NewProfilePage({ user, setUser }) {
  const { userId } = useParams();
  const [profile, setProfile] = useState(null);
  const [posts, setPosts] = useState([]);
  const [savedPosts, setSavedPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [friendsCount, setFriendsCount] = useState(0);
  
  // Highlights state
  const [showCreateHighlight, setShowCreateHighlight] = useState(false);
  const [selectedHighlight, setSelectedHighlight] = useState(null);
  const [highlightsKey, setHighlightsKey] = useState(0);
  
  // Devotees modal state
  const [showDevoteesModal, setShowDevoteesModal] = useState(false);

  const isOwnProfile = !userId || userId === user?.id;
  const profileId = userId || user?.id;
  const isOwner = isOwnProfile;

  useEffect(() => {
    if (profileId) {
      fetchProfile();
      fetchUserPosts();
      fetchFriendsCount();
      if (isOwner) {
        fetchSavedPosts();
      }
    }
  }, [profileId, isOwner]);

  const fetchFriendsCount = async () => {
    try {
      const token = localStorage.getItem('dharmbook_token');
      const endpoint = isOwner 
        ? `${API}/friends` 
        : `${API}/friends/user/${profileId}`;
      
      const response = await axios.get(endpoint, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setFriendsCount(response.data.length);
    } catch (error) {
      console.error('Failed to fetch friends count:', error);
    }
  };

  const fetchProfile = async () => {
    try {
      const token = localStorage.getItem('dharmbook_token');
      if (isOwnProfile) {
        // Fetch fresh data from API for own profile
        const response = await axios.get(`${API}/user/me`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setProfile(response.data);
        // Update local storage and user state with fresh data
        if (setUser) {
          setUser(response.data);
          localStorage.setItem('dharmbook_user', JSON.stringify(response.data));
        }
      } else {
        const response = await axios.get(`${API}/users/${profileId}`);
        setProfile(response.data);
      }
    } catch (error) {
      toast.error('Failed to load profile');
      // Fallback to local user data
      if (isOwnProfile && user) {
        setProfile(user);
      }
    }
  };

  const fetchUserPosts = async () => {
    try {
      const response = await axios.get(`${API}/users/${profileId}/posts`);
      setPosts(response.data);
    } catch (error) {
      toast.error('Failed to load posts');
    } finally {
      setLoading(false);
    }
  };

  const fetchSavedPosts = async () => {
    try {
      const token = localStorage.getItem('dharmbook_token');
      const response = await axios.get(`${API}/posts/saved`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSavedPosts(response.data);
    } catch (error) {
      console.error('Failed to load saved posts');
    }
  };

  const handleLike = async (postId) => {
    try {
      const token = localStorage.getItem('dharmbook_token');
      const response = await axios.post(
        `${API}/posts/${postId}/like`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );

      // Update posts
      setPosts(prev =>
        prev.map(post =>
          post.id === postId
            ? {
                ...post,
                is_liked: response.data.liked,
                likes_count: response.data.liked ? post.likes_count + 1 : post.likes_count - 1
              }
            : post
        )
      );

      // Update saved posts if applicable
      setSavedPosts(prev =>
        prev.map(post =>
          post.id === postId
            ? {
                ...post,
                is_liked: response.data.liked,
                likes_count: response.data.liked ? post.likes_count + 1 : post.likes_count - 1
              }
            : post
        )
      );
    } catch (error) {
      toast.error('Failed to like post');
    }
  };

  const handleSave = async (postId) => {
    try {
      const token = localStorage.getItem('dharmbook_token');
      const response = await axios.post(
        `${API}/posts/${postId}/save`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );

      // Update posts
      setPosts(prev =>
        prev.map(post =>
          post.id === postId ? { ...post, is_saved: response.data.saved } : post
        )
      );

      // Refresh saved posts if unsaving
      if (!response.data.saved && isOwner) {
        setSavedPosts(prev => prev.filter(post => post.id !== postId));
      }

      toast.success(response.data.saved ? 'Post saved' : 'Post unsaved');
    } catch (error) {
      toast.error('Failed to save post');
    }
  };

  const handlePostDelete = (postId) => {
    // Remove the deleted post from local state to update UI immediately
    setPosts(prev => prev.filter(post => post.id !== postId));
    setSavedPosts(prev => prev.filter(post => post.id !== postId));
  };

  const handlePostUpdate = () => {
    // Refresh posts after an update
    fetchUserPosts();
  };

  const handleProfileUpdate = (updatedUser) => {
    // Update profile state
    setProfile(prev => ({ ...prev, ...updatedUser }));
    // Update global user state
    if (setUser && isOwner) {
      setUser(updatedUser);
      localStorage.setItem('dharmbook_user', JSON.stringify(updatedUser));
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="text-center">
          <div className="text-4xl text-[#FF9933] mb-2">ॐ</div>
          <p className="text-slate-600">Loading profile...</p>
        </div>
      </div>
    );
  }

  if (!profile) {
    return (
      <div className="text-center py-12">
        <p className="text-slate-600">Profile not found</p>
      </div>
    );
  }

  return (
    <div className="profile-page-container" data-testid="profile-page">
      {/* Single centered column layout */}
      <div className="profile-page-layout">
        {/* Main Profile Content */}
        <div className="profile-main-content">
          <ProfileHeader 
            profile={profile} 
            isOwner={isOwner} 
            onProfileUpdate={handleProfileUpdate}
            friendsCount={friendsCount}
            currentUser={user}
            onDevoteesClick={() => setShowDevoteesModal(true)}
          />
          
          {/* Story Highlights Section */}
          <div className="border-b border-gray-100 px-4">
            <HighlightsRow
              key={highlightsKey}
              userId={profileId}
              isOwner={isOwner}
              onHighlightClick={(highlight) => setSelectedHighlight(highlight)}
              onCreateClick={() => setShowCreateHighlight(true)}
            />
          </div>
          
          {/* Mobile Devotees Section - Shows below profile info on mobile only */}
          <div className="profile-mobile-devotees">
            <FriendsList 
              userId={profileId} 
              isOwner={isOwner}
              onCountChange={setFriendsCount}
            />
          </div>
          
          <ProfileTabs 
            profile={profile} 
            isOwner={isOwner} 
            posts={posts}
            savedPosts={savedPosts}
            onLike={handleLike}
            onSave={handleSave}
            onPostDelete={handlePostDelete}
            onPostUpdate={handlePostUpdate}
            currentUserId={user?.id}
          />
        </div>
      </div>
      
      {/* Create Highlight Modal */}
      {showCreateHighlight && (
        <CreateHighlightModal
          user={user}
          onClose={() => setShowCreateHighlight(false)}
          onCreated={() => setHighlightsKey(k => k + 1)}
        />
      )}
      
      {/* Highlight Viewer */}
      {selectedHighlight && (
        <HighlightViewer
          highlight={selectedHighlight}
          onClose={() => setSelectedHighlight(null)}
        />
      )}
      
      {/* Devotees Modal - Desktop */}
      {showDevoteesModal && (
        <DevoteesModal
          userId={profileId}
          isOwner={isOwner}
          onClose={() => setShowDevoteesModal(false)}
          friendsCount={friendsCount}
        />
      )}
    </div>
  );
}
